import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { useSession } from '../context/SessionContext'
import { assignGame, fetchLeaguePlayers, uploadStateFile } from '../services/league'
import { errorMessage } from '../services/api'
import { startDiscordLogin } from '../services/auth'
import { Card, EmptyState, Notice } from '../components/ui'
import { TeamHelmet } from '../components/TeamHelmet'
import { TeamSelect } from '../components/TeamSelect'
import { TeamBoxScore } from '../components/TeamBoxScore'
import { ScoringTable } from './GameDetails'
import type { GameDetail } from '../types'
import './Upload.css'

const SAVE_ACCEPT = '.ns0,.ns1,.ns2,.ns3,.ns4,.ns5,.ns6,.ns7,.ns8,.ns9,.nst'

/** Just enough of a player to render one `<option>`. */
interface PlayerOption {
  id: number
  displayName: string
}

export default function Upload() {
  const { user, leaguePlayer, isAdmin } = useSession()
  // Only this season's conference rosters: an upload records a *current* result,
  // and the unfiltered list carries every name 30-odd imported seasons created.
  const players = useAsync(() => fetchLeaguePlayers('current'), [])

  const [file, setFile] = useState<File | null>(null)
  const [overtimeFile, setOvertimeFile] = useState<File | null>(null)
  const [doubleOvertime, setDoubleOvertime] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [game, setGame] = useState<GameDetail | null>(null)

  const [homePlayerId, setHomePlayerId] = useState<number | ''>('')
  const [awayPlayerId, setAwayPlayerId] = useState<number | ''>('')
  const [homeTeam, setHomeTeam] = useState('')
  const [awayTeam, setAwayTeam] = useState('')
  const [recap, setRecap] = useState('')

  const [saving, setSaving] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Mirrors the server rule: only a participant (or an admin) may finalize.
  const isParticipant =
    isAdmin ||
    (leaguePlayer != null &&
      (leaguePlayer.id === homePlayerId || leaguePlayer.id === awayPlayerId))

  const canSave =
    Boolean(game) &&
    homePlayerId !== '' &&
    awayPlayerId !== '' &&
    homePlayerId !== awayPlayerId &&
    isParticipant &&
    !saving

  const sortedPlayers = useMemo<PlayerOption[]>(() => {
    const options: PlayerOption[] = (players.data ?? []).map(player => ({
      id: player.id,
      displayName: player.displayName,
    }))

    // Re-uploading an existing save state replays a game that may predate this
    // season's rosters. Keep whoever is already on it selectable, so the form
    // shows what is recorded rather than an empty box over a saved assignment.
    for (const side of [game?.home, game?.away]) {
      if (side?.leaguePlayerId == null) continue
      if (options.some(option => option.id === side.leaguePlayerId)) continue
      options.push({
        id: side.leaguePlayerId,
        displayName: side.displayName ?? `Player #${side.leaguePlayerId}`,
      })
    }

    return options.sort((a, b) => a.displayName.localeCompare(b.displayName))
  }, [players.data, game])

  const rosterEmpty = !players.loading && players.error === null && sortedPlayers.length === 0

  const canParse = Boolean(file) && (!doubleOvertime || Boolean(overtimeFile))

  async function handleUpload(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    if (!file) return
    if (doubleOvertime && !overtimeFile) return

    setUploading(true)
    setUploadError(null)
    setAssignError(null)
    setSaved(false)

    try {
      const result = await uploadStateFile(file, doubleOvertime ? overtimeFile ?? undefined : undefined)
      setGame(result.game)
      setHomeTeam(result.game.home.teamAbbreviation ?? '')
      setAwayTeam(result.game.away.teamAbbreviation ?? '')
      setHomePlayerId(result.game.home.leaguePlayerId ?? '')
      setAwayPlayerId(result.game.away.leaguePlayerId ?? '')

      if (result.duplicate) {
        setUploadError('This save state was already uploaded — showing the existing game.')
      }
    } catch (caught) {
      setGame(null)
      setUploadError(errorMessage(caught, 'That save state could not be parsed.'))
    } finally {
      setUploading(false)
    }
  }

  async function handleAssign(): Promise<void> {
    if (!game || homePlayerId === '' || awayPlayerId === '') return

    setSaving(true)
    setAssignError(null)

    try {
      const updated = await assignGame(game.id, {
        homeLeaguePlayerId: Number(homePlayerId),
        awayLeaguePlayerId: Number(awayPlayerId),
        homeTeamAbbreviation: homeTeam || null,
        awayTeamAbbreviation: awayTeam || null,
        notes: recap.trim() || null,
      })
      setGame(updated)
      setSaved(true)
    } catch (caught) {
      setAssignError(errorMessage(caught, 'The result could not be saved.'))
    } finally {
      setSaving(false)
    }
  }

  if (!user) {
    return (
      <div className="stack">
        <header className="page-header">
          <div>
            <span className="eyebrow">Upload</span>
            <h1>Submit a game result</h1>
          </div>
        </header>
        <Card>
          <EmptyState title="Sign in to upload">
            <p className="muted" style={{ marginBottom: 16 }}>
              Only league members can submit results, and only a player in the game can save it.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => startDiscordLogin()}
            >
              Sign in with Discord
            </button>
          </EmptyState>
        </Card>
      </div>
    )
  }

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Upload</span>
          <h1>Submit a game result</h1>
          <p>
            Upload your Tecmo game save state file, then assign which players were P1 and P2.
            A second overtime is a second save — tick double overtime and attach both.
          </p>
        </div>
      </header>

      <Card title="1 · Save state file" subtitle="Accepts .ns0–.ns9 and .nst">
        <form onSubmit={handleUpload} className="stack-sm">
          <label className="row" style={{ alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={doubleOvertime}
              onChange={event => {
                setDoubleOvertime(event.target.checked)
                if (!event.target.checked) setOvertimeFile(null)
              }}
            />
            <span>Double overtime — this result is two save states</span>
          </label>

          <div className={doubleOvertime ? 'grid-2' : undefined}>
            <label className="dropzone">
              <input
                type="file"
                accept={SAVE_ACCEPT}
                onChange={event => setFile(event.target.files?.[0] ?? null)}
              />
              <span className="dropzone__title">
                {file
                  ? file.name
                  : doubleOvertime
                    ? 'Through first OT'
                    : 'Choose a Tecmo Super Bowl save state'}
              </span>
              <span className="dropzone__hint">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB`
                  : doubleOvertime
                    ? 'Tied game after the first overtime'
                    : 'Click to browse — maximum 5 MB'}
              </span>
            </label>

            {doubleOvertime && (
              <label className="dropzone">
                <input
                  type="file"
                  accept={SAVE_ACCEPT}
                  onChange={event => setOvertimeFile(event.target.files?.[0] ?? null)}
                />
                <span className="dropzone__title">
                  {overtimeFile ? overtimeFile.name : '2OT save'}
                </span>
                <span className="dropzone__hint">
                  {overtimeFile
                    ? `${(overtimeFile.size / 1024).toFixed(1)} KB`
                    : 'Same home and away teams, in the same order'}
                </span>
              </label>
            )}
          </div>

          <div className="row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canParse || uploading}
            >
              {uploading ? 'Parsing…' : doubleOvertime ? 'Parse both saves' : 'Parse save state'}
            </button>
            {game && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setGame(null)
                  setFile(null)
                  setOvertimeFile(null)
                  setSaved(false)
                  setUploadError(null)
                }}
              >
                Start over
              </button>
            )}
          </div>

          {uploadError && <Notice tone="error">{uploadError}</Notice>}
        </form>
      </Card>

      {game && (
        <>
          <Card
            title="2 · Parsed box score"
            subtitle={
              game.metadata?.home.secondOvertimeScore != null
                ? 'Combined from two saves — 2OT is the second file'
                : 'Read straight from the save state'
            }
          >
            {game.metadata ? (
              <div className="stack">
                <ScoringTable metadata={game.metadata} />
                {game.home.score === game.away.score && (
                  <Notice>
                    This save is tied. RTL games do not end in a tie — if this went to a second
                    overtime, start over and upload both files.
                  </Notice>
                )}
                <div className="grid-2">
                  <div className="team-panel">
                    <TeamBoxScore team={game.metadata.home} venue="home" eyebrow="Player 1 · Home" />
                  </div>
                  <div className="team-panel">
                    <TeamBoxScore team={game.metadata.away} venue="away" eyebrow="Player 2 · Away" />
                  </div>
                </div>
              </div>
            ) : (
              <EmptyState title="No box score attached to this game" />
            )}
          </Card>

          <Card
            title="3 · Assign players"
            subtitle="Match the two Discord accounts to the sides above"
          >
            <div className="stack-sm">
              {rosterEmpty && (
                <Notice tone="error">
                  Nobody is on a conference roster for the current season yet, so there is
                  nobody to assign. An admin needs to set the conferences first.
                </Notice>
              )}

              <div className="grid-2">
                <div className="stack-sm">
                  <label className="field">
                    <span className="field__label">Player 1 · Home player</span>
                    <select
                      className="select"
                      value={homePlayerId}
                      onChange={event =>
                        setHomePlayerId(event.target.value ? Number(event.target.value) : '')
                      }
                    >
                      <option value="">Select a player…</option>
                      {sortedPlayers.map(player => (
                        <option key={player.id} value={player.id}>
                          {player.displayName}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="field">
                    <span className="field__label field__label--helmet">
                      Player 1 · Tecmo team
                      <TeamHelmet abbreviation={homeTeam} size="sm" />
                    </span>
                    <TeamSelect value={homeTeam} onChange={setHomeTeam} />
                  </label>
                </div>

                <div className="stack-sm">
                  <label className="field">
                    <span className="field__label">Player 2 · Away player</span>
                    <select
                      className="select"
                      value={awayPlayerId}
                      onChange={event =>
                        setAwayPlayerId(event.target.value ? Number(event.target.value) : '')
                      }
                    >
                      <option value="">Select a player…</option>
                      {sortedPlayers.map(player => (
                        <option key={player.id} value={player.id}>
                          {player.displayName}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="field">
                    <span className="field__label field__label--helmet">
                      Player 2 · Tecmo team
                      <TeamHelmet abbreviation={awayTeam} size="sm" />
                    </span>
                    <TeamSelect value={awayTeam} onChange={setAwayTeam} />
                  </label>
                </div>
              </div>

              <label className="field">
                <span className="field__label">Game recap or comments</span>
                <textarea
                  className="input"
                  rows={3}
                  maxLength={2000}
                  value={recap}
                  placeholder="Optional — how the game went, notable plays, bad luck…"
                  onChange={event => setRecap(event.target.value)}
                />
                <span className="field__hint">Shown on the game page.</span>
              </label>

              {homePlayerId !== '' && awayPlayerId !== '' && !isParticipant && (
                <Notice tone="error">
                  You are not one of these two players, so you cannot save this result. Ask a
                  participant or an admin to finalize it.
                </Notice>
              )}

              {assignError && <Notice tone="error">{assignError}</Notice>}

              {saved && (
                <Notice tone="success">
                  Result saved and marked official.{' '}
                  <Link to={`/games/${game.id}`} style={{ textDecoration: 'underline' }}>
                    View the game
                  </Link>
                  .
                </Notice>
              )}

              <div className="row">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!canSave}
                  onClick={handleAssign}
                >
                  {saving ? 'Saving…' : 'Save result'}
                </button>
                <span className="muted" style={{ fontSize: '0.82rem' }}>
                  Final score {game.home.score}–{game.away.score}
                </span>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
